import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { formatShortDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  type PageMeta,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { APPLICATION_STATUS } from '@/lib/status';

interface ApplicationRow {
  id: string;
  applicationNumber: string;
  memberName: string;
  membershipNumber: string;
  productName: string;
  requestedAmount: string;
  approvedAmount: string | null;
  installmentCount: number;
  status: string;
  submittedAt: string | null;
  guarantorsConfirmed: number;
  guarantorsRequired: number;
}

export default function AdminApplications() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['applications', page, status],
    queryFn: () =>
      api.get<{ data: ApplicationRow[]; meta: PageMeta }>(
        `/loan-applications?pageSize=25&page=${page}${status ? `&status=${status}` : ''}`,
      ),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Loan operations"
        title="Applications"
        description="Review, approve or decline member loan applications."
        actions={
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="w-56">
            <option value="">All statuses</option>
            {Object.entries(APPLICATION_STATUS).map(([key, presentation]) => (
              <option key={key} value={key}>
                {presentation.label}
              </option>
            ))}
          </Select>
        }
      />

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          icon={<FileText size={20} />}
          title="No applications"
          description="Applications appear here once members submit them."
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Application</Th>
                  <Th>Member</Th>
                  <Th>Product</Th>
                  <Th numeric>Requested</Th>
                  <Th numeric>Payments</Th>
                  <Th>Guarantors</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.data.map((application) => {
                  const guarantorsReady =
                    application.guarantorsConfirmed >= application.guarantorsRequired;
                  return (
                    <tr
                      key={application.id}
                      onClick={() => navigate(`/admin/applications/${application.id}`)}
                      className="cursor-pointer transition-colors hover:bg-muted/50"
                    >
                      <Td className="numeric font-semibold">{application.applicationNumber}</Td>
                      <Td>
                        <div>
                          <p className="font-medium text-foreground">{application.memberName}</p>
                          <p className="numeric text-xs text-foreground-secondary">
                            {application.membershipNumber}
                          </p>
                        </div>
                      </Td>
                      <Td className="text-foreground-secondary">{application.productName}</Td>
                      <Td numeric>{money(application.requestedAmount)}</Td>
                      <Td numeric>{application.installmentCount}</Td>
                      <Td>
                        {/* Surfaced in the queue so a reviewer can see at a
                            glance whether an application is decidable at all. */}
                        <span
                          className={`numeric text-sm font-semibold ${
                            guarantorsReady ? 'text-success' : 'text-warning'
                          }`}
                        >
                          {application.guarantorsConfirmed}/{application.guarantorsRequired}
                        </span>
                      </Td>
                      <Td>
                        <StatusBadge map={APPLICATION_STATUS} status={application.status} />
                      </Td>
                      <Td className="numeric text-foreground-secondary">
                        {application.submittedAt
                          ? formatShortDate(new Date(application.submittedAt))
                          : '—'}
                      </Td>
                      <Td>
                        {['SUBMITTED', 'UNDER_REVIEW', 'AWAITING_GUARANTORS'].includes(
                          application.status,
                        ) && (
                          <Button size="sm" variant="outline">
                            Review
                          </Button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}

    </div>
  );
}
